import { z } from 'zod';
import { EntityIdSchema, IsoDateTimeSchema } from './common';

/**
 * Input para POST /api/v1/dispositivos/registrar (móvil).
 * Backend exige los 3 campos (modelo, os_version, app_version) no vacíos.
 */
export const DispositivoRegistrarInputSchema = z.object({
  modelo: z.string().min(1),
  os_version: z.string().min(1),
  app_version: z.string().min(1),
});
export type DispositivoRegistrarInput = z.infer<typeof DispositivoRegistrarInputSchema>;

/**
 * Response de POST /api/v1/dispositivos/registrar.
 */
export const DispositivoRegistrarResponseSchema = z.object({
  device_id: EntityIdSchema,
  trust_token: z.string(),
});
export type DispositivoRegistrarResponse = z.infer<typeof DispositivoRegistrarResponseSchema>;

/**
 * Dispositivo registrado por una persona en el canal móvil.
 * `revocado` es booleano en la API (la BD guarda 0/1).
 */
export const DispositivoRowSchema = z.object({
  id: z.string(),
  modelo: z.string().nullable(),
  os_version: z.string().nullable(),
  app_version: z.string().nullable(),
  registrado_en: z.string(),
  ultima_sync_en: z.string().nullable(),
  revocado: z.boolean(),
});
export type DispositivoRow = z.infer<typeof DispositivoRowSchema>;

/**
 * Response de GET /api/v1/usuarios/:id/dispositivos.
 */
export const UsuarioDispositivosResponseSchema = z.object({
  data: z.array(DispositivoRowSchema),
});
export type UsuarioDispositivosResponse = z.infer<typeof UsuarioDispositivosResponseSchema>;

/**
 * Body de POST /api/v1/usuarios/:id/cohortes: cohortes a asignar a la persona
 * en el canal móvil (`cohorte_operador`).
 */
export const CohorteOperadorInputSchema = z.object({
  cohorte_ids: z.array(EntityIdSchema),
});
export type CohorteOperadorInput = z.infer<typeof CohorteOperadorInputSchema>;

/**
 * Body de POST /api/v1/cohortes/:id/operadores: operadores a asignar a la
 * cohorte en el canal móvil (`cohorte_operador`). Es el inverso de
 * `CohorteOperadorInputSchema` (que asigna cohortes a una persona).
 *
 * Esta asignación es la que hace que el móvil reciba su cohorte al arrancar:
 * `GET /api/v1/dispositivos/:id/cohorte-activa` busca la cohorte activa del
 * operador por esta tabla. Sin filas, el móvil no trae participantes ni
 * sesiones y su sincronización post-login queda vacía.
 */
export const CohorteOperadoresInputSchema = z.object({
  operador_ids: z.array(EntityIdSchema),
});
export type CohorteOperadoresInput = z.infer<typeof CohorteOperadoresInputSchema>;

/**
 * Snapshot de cohorte activa que el móvil descarga (GET /api/v1/dispositivos/:id/cohorte-activa).
 * Backend actual NO devuelve este snapshot tipado; placeholder.
 */
export const CohorteSnapshotSchema = z.object({
  cohorte_id: EntityIdSchema,
  programa_id: EntityIdSchema,
  fecha_inicio: z.string(),
  fecha_fin: z.string(),
  generated_at: IsoDateTimeSchema,
});
export type CohorteSnapshot = z.infer<typeof CohorteSnapshotSchema>;